import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FiImage, FiPaperclip, FiSend, FiVideo } from 'react-icons/fi'
import ReactQuill from 'react-quill'
import { toast } from 'react-toastify'

import './ticket-attachment-blot'
import {
  DOCUMENT_ACCEPT,
  getAttachmentKind,
  MAX_MESSAGE_FILE_SIZE,
  MAX_MESSAGE_FILES,
  normalizeAttachmentFile,
} from './ticket-reply-attachments.utils'

import 'react-quill/dist/quill.snow.css'

const editorFormats = ['ticketAttachment']

const toolbarButtonClass =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50'

const HelpDeskReplyComposer = ({
  messageBody,
  messageHtml,
  messageAttachments,
  isSending,
  onMessageChange,
  onMessageAttachmentsChange,
  onSendMessage,
}) => {
  const { t } = useTranslation()
  const editorRef = useRef(null)
  const imageInputRef = useRef(null)
  const videoInputRef = useRef(null)
  const fileInputRef = useRef(null)

  const editorModules = useMemo(() => ({ toolbar: false }), [])

  const insertAttachments = (selectedFiles) => {
    if (!selectedFiles?.length) return

    const activeAttachments = messageAttachments.filter((attachment) => messageHtml.includes(attachment.localId))
    const nextAttachments = []

    for (const selectedFile of Array.from(selectedFiles)) {
      if (activeAttachments.length + nextAttachments.length >= MAX_MESSAGE_FILES) {
        toast.error(t('helpdesk.form.attachments.max_files', { count: MAX_MESSAGE_FILES }))
        break
      }

      const file = normalizeAttachmentFile(selectedFile)
      const kind = getAttachmentKind(file)
      if (!kind) {
        toast.error(t('helpdesk.form.attachments.invalid_type'))
        continue
      }

      if (file.size > MAX_MESSAGE_FILE_SIZE) {
        toast.error(t('helpdesk.form.attachments.max_size', { size: 50 }))
        continue
      }

      const isDuplicate = [...activeAttachments, ...nextAttachments].some(
        (attachment) => attachment.file.name === file.name && attachment.file.size === file.size,
      )
      if (isDuplicate) {
        toast.error(t('helpdesk.form.attachments.duplicate'))
        continue
      }

      nextAttachments.push({
        localId: window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        kind,
      })
    }

    const editor = editorRef.current?.getEditor()
    if (!editor || !nextAttachments.length) {
      nextAttachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl))
      return
    }

    let insertAt = editor.getSelection(true)?.index ?? Math.max(0, editor.getLength() - 1)
    nextAttachments.forEach((attachment) => {
      editor.insertEmbed(
        insertAt,
        'ticketAttachment',
        {
          localId: attachment.localId,
          previewUrl: attachment.previewUrl,
          name: attachment.file.name,
          mimeType: attachment.file.type,
          kind: attachment.kind,
          linkText: t('support.detail.attachment_named', { name: attachment.file.name }),
        },
        'user',
      )
      editor.insertText(insertAt + 1, '\n', 'user')
      insertAt += 2
    })
    editor.setSelection(insertAt, 0, 'silent')
    onMessageAttachmentsChange([...activeAttachments, ...nextAttachments])
  }

  const resetInput = (input) => {
    input.value = ''
  }

  return (
    <form onSubmit={onSendMessage} className="space-y-3 border-t border-slate-100 pt-4">
      <div>
        <label htmlFor="helpdesk-reply" className="mb-1 block text-sm font-medium text-slate-700">
          {t('support.detail.reply')}
        </label>
        <div className="flex items-center gap-1 rounded-t-lg border border-b-0 border-slate-200 bg-white px-2 py-1">
          <button
            type="button"
            className={toolbarButtonClass}
            title={t('support.detail.add_image')}
            disabled={isSending}
            onClick={() => imageInputRef.current?.click()}
          >
            <FiImage className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={toolbarButtonClass}
            title={t('support.detail.add_video')}
            disabled={isSending}
            onClick={() => videoInputRef.current?.click()}
          >
            <FiVideo className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={toolbarButtonClass}
            title={t('support.detail.add_file')}
            disabled={isSending}
            onClick={() => fileInputRef.current?.click()}
          >
            <FiPaperclip className="h-4 w-4" />
          </button>
        </div>
        <ReactQuill
          ref={editorRef}
          id="helpdesk-reply"
          theme="snow"
          value={messageHtml}
          modules={editorModules}
          formats={editorFormats}
          onChange={(html, _delta, _source, editor) => {
            const parsedHtml = new DOMParser().parseFromString(html, 'text/html')
            const activeAttachmentIds = Array.from(parsedHtml.querySelectorAll('[data-local-id]'))
              .map((element) => element.dataset.localId)
              .filter(Boolean)

            onMessageChange({
              html,
              text: editor.getText().replace(/\n$/, ''),
              activeAttachmentIds,
            })
          }}
          placeholder={t('support.detail.reply_placeholder')}
          readOnly={isSending}
          className="bg-white text-sm [&_.ql-container]:min-h-[180px] [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[180px] [&_.ticket-inline-attachment]:my-3 [&_.ticket-inline-attachment]:rounded-lg [&_.ticket-inline-attachment]:border [&_.ticket-inline-attachment]:border-slate-200 [&_.ticket-inline-attachment]:bg-slate-50 [&_.ticket-inline-attachment]:p-2 [&_.ticket-inline-attachment_img]:mx-auto [&_.ticket-inline-attachment_img]:max-h-80 [&_.ticket-inline-attachment_img]:max-w-full [&_.ticket-inline-attachment_img]:rounded-md [&_.ticket-inline-attachment_video]:mx-auto [&_.ticket-inline-attachment_video]:max-h-80 [&_.ticket-inline-attachment_video]:max-w-full [&_.ticket-inline-attachment_figcaption]:mt-1 [&_.ticket-inline-attachment_figcaption]:truncate [&_.ticket-inline-attachment_figcaption]:text-xs [&_.ticket-inline-attachment_figcaption]:text-slate-500"
        />
        <input
          ref={imageInputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg"
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          onChange={(event) => {
            insertAttachments(event.target.files)
            resetInput(event.target)
          }}
        />
        <input
          ref={videoInputRef}
          type="file"
          multiple
          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          onChange={(event) => {
            insertAttachments(event.target.files)
            resetInput(event.target)
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={DOCUMENT_ACCEPT}
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          onChange={(event) => {
            insertAttachments(event.target.files)
            resetInput(event.target)
          }}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="submit"
          disabled={isSending || (!messageBody.trim() && messageAttachments.length === 0)}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#006EAB] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#005a8f] disabled:opacity-60"
        >
          <FiSend className="h-4 w-4" />
          {isSending ? t('support.detail.sending') : t('support.detail.send_comment')}
        </button>
      </div>
    </form>
  )
}

export default HelpDeskReplyComposer
